import Validator from 'validatorjs';
import config from '_config';
import axios from 'axios';
import auth from './auth';

window.isAlreadyFetchingAccessToken = false;

class BaseService {
  getUrl() {
    return config.API_URL;
  }

  request(
    url = this.getUrl(),
    data = null,
    method = 'get',
    responseType = null,
    headers = {
      Accept: 'application/json',
    },
  ) {
    const params = method === 'get' ? data : {};

    return axios({
      method,
      url,
      data,
      params,
      responseType,
      headers,
    })
      .then(response => this.handleResponse(response))
      .catch(error => this.handleError(error));
  }

  validate(data, rules, names = null) {
    const validation = new Validator(data, rules);

    if (names !== null) validation.setAttributeNames(names);

    if (validation.fails()) {
      Object.keys(validation.errors.all()).forEach(error =>
        Promise.reject(
          new Error({
            error_description: validation.errors.all()[error][0],
          }),
        ),
      );
    }

    return Promise.resolve();
  }

  createFormData(data, splitArray = false, arrayIndexProp = []) {
    const fd = new FormData();

    Object.keys(data).forEach(key => {
      const item = data[key];

      if (typeof item === 'boolean') {
        fd.append(key, item ? 1 : 0);
      } else if (splitArray && Array.isArray(item)) {
        const isIndexProp = arrayIndexProp.find(i => i === key);

        Object.keys(item).forEach(index => {
          fd.append(
            `${key}[${index}]${isIndexProp ? '[_id]' : ''}`,
            item[index],
          );
        });
      } else {
        fd.append(key, item);
      }
    });

    return fd;
  }

  handleResponse(response) {
    return Object.assign(response.data, {
      message: response.data.message || response.message,
    });
  }

  rejectAsError(payload, status = null) {
    if (payload instanceof Error) {
      if (status != null && payload.status == null) {
        payload.status = status;
      }
      return Promise.reject(payload);
    }

    const message =
      (payload && typeof payload.message === 'string' && payload.message) ||
      (payload &&
        typeof payload.error_description === 'string' &&
        payload.error_description) ||
      'Request failed';
    const err = new Error(message);

    if (payload && typeof payload === 'object') {
      Object.assign(err, payload);
      err.data = payload.data || payload.errors || payload;
    }

    if (status != null) {
      err.status = status;
    }

    return Promise.reject(err);
  }

  handleError(error) {
    const { response } = error;
    const status = response ? response.status : null;
    const data = response ? response.data : error;

    if (status === 401) {
      if (!window.isAlreadyFetchingAccessToken) {
        window.isAlreadyFetchingAccessToken = true;
        const lastRequestConfig = error.config;

        return auth
          .refreshToken()
          .then(res => {
            window.isAlreadyFetchingAccessToken = false;
            lastRequestConfig.headers.Authorization = `Bearer ${res.token}`;
            return axios(lastRequestConfig).then(lastRequestResponse =>
              Promise.resolve(lastRequestResponse.data),
            );
          })
          .catch(err => {
            window.isAlreadyFetchingAccessToken = false;
            window.location.href = '/public/login';
            return Promise.reject(err);
          });
      }
      return this.rejectAsError(data, status);
    }

    if (status === 403) {
      auth.resetUser(data);
    }

    return this.rejectAsError(data, status);
  }

  setParamsToIfPdf(pagination, pdf) {
    const parameters = {
      params: pagination,
      responseType: 'json',
    };

    if (pdf) {
      parameters.responseType = 'arraybuffer';
    }
    return parameters;
  }

  setFormData(object, formData, parent = '') {
    if (object) {
      Object.entries(object).map(field => {
        let label = field[0];
        if (parent !== '') {
          label = `${parent}[${field[0]}]`;
        }
        if (typeof field[1] === 'object' && !(field[1] instanceof File)) {
          return this.setFormData(field[1], formData, label);
        }
        formData.append(label, field[1]);
        return field;
      });
    }
    return formData;
  }
}

export default BaseService;
